import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { http } from '@/lib/http';
import type { FlightDataSource } from '@/lib/flights/types';

// "Add PNRs" — round 4 of the case-management family: pull manifest PNRs into
// an EXISTING case that were left out at creation (narrowed selection, excluded
// at intake, or arrived later on the manifest). The BFF proxies to cases
// `GET /v1/cases/:id/addable-pnrs` + `POST /v1/cases/:id/pnrs`. The addable
// list is grounded in a REAL manifest read: `manifestAvailable: false` means
// "nothing to offer from" (a selection-born case has no flight, or the manifest
// is unreachable) — never a synthetic list. Rows already excluded from the case
// come back flagged so the operator can re-add them deliberately.

export interface AddablePnr {
  pnrUrn: string;
  /** Airline record locator when the manifest carries one. */
  locator?: string;
  paxCount: number;
  /** MASKED per the roster masking rules — never a raw name. */
  leadPassenger?: string;
  /** Had a sub-case and was excluded — re-adding reverts that sub-case. */
  alreadyExcluded: boolean;
  /**
   * Where the airline says this PNR came from — an imported file, the airline, or generated
   * data (`SYNTHETIC`: shown «Sintético», e.g. a mocked airline adapter's PNRs on a demo).
   * Absent when the airline says nothing. Additive.
   */
  source?: Extract<FlightDataSource, 'FILE' | 'SERVICE_PROVIDER' | 'SYNTHETIC'>;
}

export interface AddablePnrsResult {
  caseUrn: string;
  pnrs: AddablePnr[];
  /** False → no flight on the case OR the manifest read failed; list is empty. */
  manifestAvailable: boolean;
}

export async function fetchAddablePnrs(caseUrn: string): Promise<AddablePnrsResult> {
  return http
    .get(`v1/cases/${encodeURIComponent(caseUrn)}/addable-pnrs`)
    .json<AddablePnrsResult>();
}

export type AddPnrSkipReason = 'already_in_case' | 'not_on_manifest';

export interface AddPnrsResult {
  caseUrn: string;
  added: number;
  skipped: { pnrUrn: string; reason: AddPnrSkipReason }[];
}

export async function addPnrsToCase(caseUrn: string, pnrUrns: string[]): Promise<AddPnrsResult> {
  return http
    .post(`v1/cases/${encodeURIComponent(caseUrn)}/pnrs`, { json: { pnrUrns } })
    .json<AddPnrsResult>();
}

/**
 * The addable list, fetched only while the dialog is open (`open` gates the
 * query). Default staleness (0) means every reopen re-reads the manifest — the
 * list must reflect what is addable NOW, not what was when the tab mounted.
 */
export function useAddablePnrs(caseUrn: string, open: boolean) {
  return useQuery<AddablePnrsResult>({
    queryKey: ['cases', caseUrn, 'addable-pnrs'],
    queryFn: () => fetchAddablePnrs(caseUrn),
    enabled: open && !!caseUrn,
  });
}

export function useAddPnrs(caseUrn: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (pnrUrns: string[]) => addPnrsToCase(caseUrn, pnrUrns),
    onSuccess: () => {
      // Added groups appear on the roster, move the allocation numbers and the
      // header pax count — invalidate the three case-scoped reads, plus the
      // addable list itself so a reopened dialog reflects what just landed.
      void qc.invalidateQueries({ queryKey: ['cases', caseUrn, 'pnr-groups'] });
      void qc.invalidateQueries({ queryKey: ['cases', caseUrn, 'allocation'] });
      void qc.invalidateQueries({ queryKey: ['cases', caseUrn, 'overview'] });
      void qc.invalidateQueries({ queryKey: ['cases', caseUrn, 'addable-pnrs'] });
    },
  });
}
